import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { SearchableSelect } from '@/components/SearchableSelect';
import { sortSections } from '@/lib/sectionSort';

const leadLabel = (lead) => {
  const name = (lead.contact_name || lead.company || '').trim();
  const addr = (lead.project_address || '').trim();
  if (name && addr) return `${name} - ${addr}`;
  return name || addr || lead.id?.slice(0, 8) || 'Lead';
};

const rfpLabel = (rfp) => {
  const agency = (rfp.agency || '').trim();
  const title = (rfp.bid_title || '').trim();
  if (agency && title) return `${agency} - ${title}`;
  return agency || title || rfp.id?.slice(0, 8) || 'RFP';
};

export function EntryFormSheet({
  open, onOpenChange, editEntry, entryForm, setEntryForm,
  projects, proposals, filteredProposals, currentSections,
  leads = [], rfps = [],
  showClosedProposals, setShowClosedProposals,
  handleProjectChange, handleProposalChange,
  handleLeadChange, handleRfpChange,
  saveEntry
}) {
  // Determine which link is "active" so the other dropdowns can be disabled
  // (mirrors the backend's mutual exclusivity rule).
  const hasProject = !!entryForm.project_id;
  const hasProposal = !!entryForm.proposal_id;
  const hasLead = !!entryForm.lead_id;
  const hasRfp = !!entryForm.rfp_id;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading">{editEntry ? 'Edit Time Entry' : 'Log Hours'}</SheetTitle>
          <SheetDescription>
            {editEntry ? 'Update this time entry' : 'Log hours against a Project, Proposal, Lead, or RFP'}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-3 mt-4">
          <div>
            <Label className="text-xs text-muted-foreground">Date *</Label>
            <Input type="date" value={entryForm.date} onChange={e => setEntryForm(f => ({ ...f, date: e.target.value }))} className="bg-background border-border" data-testid="entry-date" />
          </div>

          {/* Project */}
          <div>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground">Project <span className="text-amber-400">*</span></Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground/70 cursor-help" data-testid="entry-project-info" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-popover text-popover-foreground border-border max-w-xs p-3 shadow-lg">
                    <p className="text-xs leading-relaxed">
                      If your timesheet entry isn&apos;t for a client project, an internal job must be selected. If the internal job section does not exist, you may create one by editing the project on the Contracts page. Make sure to check other Internal Job Projects before adding new sections.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-xs text-muted-foreground/60 font-normal italic ml-1">or pick one of the alternatives below</span>
            </div>
            {hasProposal || hasLead || hasRfp ? (
              <Select value={entryForm.project_id || 'none'} disabled>
                <SelectTrigger className="bg-background border-border" data-testid="entry-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">No Project</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <SearchableSelect
                value={entryForm.project_id || ''}
                onValueChange={v => handleProjectChange(v)}
                options={[{ value: '', label: 'No Project' }, ...projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))]}
                placeholder="Select project"
                searchPlaceholder="Search projects..."
                data-testid="entry-project"
              />
            )}
          </div>

          {/* Proposal */}
          <div>
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">Proposal <span className="text-xs text-muted-foreground/60 font-normal italic">or link to a proposal instead</span></Label>
            {hasProject || hasLead || hasRfp ? (
              <Select value={entryForm.proposal_id || 'none'} disabled>
                <SelectTrigger className="bg-background border-border" data-testid="entry-proposal"><SelectValue placeholder="Select proposal" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">No Proposal</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <SearchableSelect
                value={entryForm.proposal_id || ''}
                onValueChange={v => handleProposalChange(v)}
                options={[{ value: '', label: 'No Proposal' }, ...filteredProposals.map(p => {
                  const clientLabel = p.client_name || p.contact_name || '';
                  return { value: p.id, label: clientLabel ? `${p.proposal_number} · ${clientLabel}` : p.proposal_number };
                })]}
                placeholder="Select proposal"
                searchPlaceholder="Search proposals..."
                data-testid="entry-proposal"
              />
            )}
          </div>

          {/* Lead */}
          <div>
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">Lead <span className="text-xs text-muted-foreground/60 font-normal italic">or link to a lead</span></Label>
            {hasProject || hasProposal || hasRfp ? (
              <Select value={entryForm.lead_id || 'none'} disabled>
                <SelectTrigger className="bg-background border-border" data-testid="entry-lead"><SelectValue placeholder="Select lead" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">No Lead</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <SearchableSelect
                value={entryForm.lead_id || ''}
                onValueChange={v => handleLeadChange(v)}
                options={[{ value: '', label: 'No Lead' }, ...leads.map(l => ({ value: l.id, label: leadLabel(l) }))]}
                placeholder="Select lead"
                searchPlaceholder="Search leads..."
                data-testid="entry-lead"
              />
            )}
          </div>

          {/* RFP */}
          <div>
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">RFP <span className="text-xs text-muted-foreground/60 font-normal italic">or link to an RFP opportunity</span></Label>
            {hasProject || hasProposal || hasLead ? (
              <Select value={entryForm.rfp_id || 'none'} disabled>
                <SelectTrigger className="bg-background border-border" data-testid="entry-rfp"><SelectValue placeholder="Select RFP" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">No RFP</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <SearchableSelect
                value={entryForm.rfp_id || ''}
                onValueChange={v => handleRfpChange(v)}
                options={[{ value: '', label: 'No RFP' }, ...rfps.map(r => ({ value: r.id, label: rfpLabel(r) }))]}
                placeholder="Select RFP"
                searchPlaceholder="Search RFPs..."
                data-testid="entry-rfp"
              />
            )}
          </div>

          {/* Section — only meaningful when linked to a project */}
          {hasProject && !hasProposal && !hasLead && !hasRfp && (
            <div>
              <Label className="text-xs text-muted-foreground">Section *</Label>
              <Select value={entryForm.contract_section_id || 'none'} onValueChange={v => setEntryForm(f => ({ ...f, contract_section_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="bg-background border-border" data-testid="entry-section"><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">Select section...</SelectItem>
                  {sortSections(currentSections).map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.section_number ? `${s.section_number} - ` : ''}{s.section_name}{s.source_tag && s.source_tag !== 'Original Contract' ? ` [${s.source_tag}]` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs text-muted-foreground">Hours *</Label>
            <Input type="number" step="0.25" min="0" max="24" value={entryForm.hours} onChange={e => setEntryForm(f => ({ ...f, hours: e.target.value }))} className="bg-background border-border" placeholder="0.0" data-testid="entry-hours" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Notes *</Label>
            <RichTextEditor value={entryForm.description} onChange={v => setEntryForm(f => ({ ...f, description: v }))} placeholder="What did you work on?" data-testid="entry-description" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="billable"
              checked={entryForm.is_billable}
              onCheckedChange={v => setEntryForm(f => ({ ...f, is_billable: v }))}
              data-testid="entry-billable"
            />
            <Label htmlFor="billable" className="text-xs text-muted-foreground cursor-pointer">Billable</Label>
          </div>
          {!entryForm.is_billable && (
            <p className="text-xs text-amber-400/80 bg-amber-500/5 border border-amber-500/15 rounded px-2.5 py-1.5 leading-relaxed" data-testid="billable-reminder">
              Only mark as non-billable for breaks or time the company should not pay you for. All internal and external work should remain billable.
            </p>
          )}
          {/* Closed proposals helper — only visible while user is choosing a proposal */}
          {(!hasProject && !hasLead && !hasRfp) && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-closed-proposals"
                checked={showClosedProposals}
                onCheckedChange={v => setShowClosedProposals(!!v)}
                data-testid="show-closed-proposals"
              />
              <Label htmlFor="show-closed-proposals" className="text-xs text-muted-foreground cursor-pointer">Show closed proposals</Label>
            </div>
          )}
          <Button onClick={saveEntry} className="w-full bg-primary text-primary-foreground mt-2" data-testid="save-entry-btn">
            {editEntry ? 'Update Entry' : 'Log Hours'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
